/**
 * Stage IG redesign Phase 3:コメント管理(spec §8)。
 *
 * 2 欄レイアウト(spec §8.1):
 *   - 左:コメントキュー(checkbox / 投稿サムネ / アバター / ユーザー / 本文 / 時間 / status)
 *   - 右:原の投稿 + 選択中コメント + 返信履歴 + 返信を作成 + アクション + 内部メモ
 *
 * spec §8.3:
 *   - 操作ボタンは backend で本当にサポートされているもののみ呼ぶ
 *   - 未実装のボタン(スパム報告 / 非表示 / 削除 / AI 返信 / 状態変更)は
 *     disabled + PENDING_FEATURE_HINT 表示
 *   - 偽の成功 toast は出さない
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Image as ImageIcon,
  MessageCircle,
  Search,
  Send,
  Sparkles,
  ThumbsUp,
  Trash2,
} from "lucide-react";

import {
  AccountChip,
  EmptyState,
  ErrorState,
  InstagramSubnav,
  LoadingState,
  RefreshButton,
  SectionCard,
  SegmentedControl,
  STATUS_LABEL,
  StatusBadge,
} from "./components/common";
import { useInstagramComments } from "./hooks/useInstagram";
import type { CommentItem, CommentType, WorkflowStatus } from "./types";
import { formatRelativeTime, getInitial } from "./utils/format";
import "./instagram.css";

const PENDING_FEATURE_HINT =
  "送信 / 非表示 / 状態変更は次のステップ（P1-C）で有効になります。";

type StatusFilterKey = "all" | "pending" | "done" | "spam";

const STATUS_FILTERS: { key: StatusFilterKey; label: string }[] = [
  { key: "all", label: "すべて" },
  { key: "pending", label: "未対応" },
  { key: "done", label: "対応済み" },
  { key: "spam", label: "スパム" },
];

const COMMENT_TYPE_LABEL: Record<CommentType, string> = {
  comment: "コメント",
  mention: "メンション",
  reply: "返信",
};

// ---------------------------------------------------------------------------
// Comment row(spec §8.2)
// ---------------------------------------------------------------------------

function CommentRow({
  item,
  active,
  onSelect,
}: {
  item: CommentItem;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className={`ig-comment-row${active ? " is-selected" : ""}`}
      onClick={() => onSelect(item.id)}
    >
      <input
        type="checkbox"
        aria-label="選択"
        style={{ accentColor: "var(--ig-primary-2)" }}
        onClick={(e) => e.stopPropagation()}
      />
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 10,
          background: "#f3f4f6",
          overflow: "hidden",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
        }}
      >
        {item.media_thumbnail_url ? (
          <img
            src={item.media_thumbnail_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <ImageIcon size={20} color="#9ca3af" />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "var(--ig-surface-soft)",
              color: "var(--ig-text)",
              display: "grid",
              placeItems: "center",
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            {getInitial(item.user_name)}
          </div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ig-text)",
            }}
          >
            @{item.user_name}
          </span>
          <span style={{ fontSize: 10, color: "var(--ig-muted)" }}>
            {formatRelativeTime(item.created_at)}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--ig-text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.45,
          }}
        >
          {item.text}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 6,
          }}
        >
          <StatusBadge status={item.status} />
          <span
            style={{
              fontSize: 10,
              padding: "1px 6px",
              borderRadius: 4,
              background: "var(--ig-surface-soft)",
              color: "var(--ig-muted)",
            }}
          >
            {COMMENT_TYPE_LABEL[item.comment_type] ?? item.comment_type}
          </span>
          {item.like_count > 0 ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10,
                color: "var(--ig-muted)",
              }}
            >
              <ThumbsUp size={10} /> {item.like_count}
            </span>
          ) : null}
          {(item.replies ?? []).length > 0 ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10,
                color: "var(--ig-muted)",
              }}
            >
              <MessageCircle size={10} /> {item.replies.length}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InstagramCommentsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>("all");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const list = useInstagramComments({
    status: statusFilter === "all" ? undefined : statusFilter,
    q: query,
  });
  const items = list.data?.items ?? [];
  const counts = list.data?.counts ?? {};

  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  useEffect(() => {
    setDraftText("");
  }, [selectedId]);

  const current = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  return (
    <div className="ig-page">
      <CommentsHero
        accountName={current?.user_name ? "@" + current.user_name : null}
        connected={Boolean(current)}
        onRefresh={list.refresh}
        loading={list.loading}
      />

      <div className="ig-comments-workspace">
        {/* ---- 左:コメントキュー ---- */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div
            style={{
              padding: "16px 18px 12px",
              borderBottom: "1px solid var(--ig-divider)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--ig-text)",
                marginBottom: 10,
              }}
            >
              受信コメント
            </div>
            <SegmentedControl
              items={STATUS_FILTERS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <div style={{ position: "relative", marginTop: 12 }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  top: 13,
                  left: 10,
                  color: "var(--ig-muted)",
                }}
              />
              <input
                className="ig-input"
                placeholder="ユーザー名・本文で検索"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ paddingLeft: 30 }}
              />
            </div>
          </div>

          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {list.error ? (
              <ErrorState
                message={list.error}
                code={list.errorCode}
                onRetry={list.refresh}
              />
            ) : list.loading && items.length === 0 ? (
              <LoadingState label="コメントを読み込んでいます..." />
            ) : items.length === 0 ? (
              <EmptyState
                title="コメントがありません"
                description={
                  query || statusFilter !== "all"
                    ? "条件に一致するコメントは見つかりませんでした。"
                    : "新しいコメントが届くとここに表示されます。"
                }
              />
            ) : (
              items.map((item) => (
                <CommentRow
                  key={item.id}
                  item={item}
                  active={item.id === selectedId}
                  onSelect={setSelectedId}
                />
              ))
            )}
          </div>
        </div>

        {/* ---- 右:返信エリア ---- */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderLeft: "1px solid var(--ig-divider)",
          }}
        >
          {!current ? (
            <EmptyState
              title="コメントを選択してください"
              description="左のリストからコメントを選ぶと、返信エリアが表示されます。"
            />
          ) : (
            <div
              style={{
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* 元の投稿 */}
              <SectionCard title="元の投稿" flush>
                <div style={{ display: "flex", gap: 12, padding: 14 }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 10,
                      background: "#f3f4f6",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {current.media_thumbnail_url ? (
                      <img
                        src={current.media_thumbnail_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <ImageIcon size={20} color="#9ca3af" />
                      </div>
                    )}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ig-text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {current.media_caption || "(キャプションなし)"}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--ig-muted)",
                        marginTop: 4,
                      }}
                    >
                      Media ID: {current.media_id}
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* 選択中コメント */}
              <SectionCard title="選択中のコメント">
                <div style={{ display: "flex", gap: 10 }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "var(--ig-surface-soft)",
                      color: "var(--ig-text)",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {getInitial(current.user_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--ig-text)",
                      }}
                    >
                      @{current.user_name}{" "}
                      <span
                        style={{
                          fontWeight: 400,
                          color: "var(--ig-muted)",
                          marginLeft: 4,
                        }}
                      >
                        {COMMENT_TYPE_LABEL[current.comment_type] ??
                          current.comment_type}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ig-text)",
                        marginTop: 4,
                        lineHeight: 1.55,
                      }}
                    >
                      {current.text}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--ig-muted)",
                        marginTop: 6,
                      }}
                    >
                      {formatRelativeTime(current.created_at)}
                      {current.like_count > 0
                        ? ` · ♥ ${current.like_count}`
                        : ""}
                    </div>
                  </div>
                  <StatusBadge status={current.status} />
                </div>
              </SectionCard>

              {/* 返信履歴 */}
              {(current.replies ?? []).length > 0 ? (
                <SectionCard title="返信履歴">
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {current.replies!.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          gap: 10,
                          padding: 10,
                          background: "var(--ig-surface-soft)",
                          borderRadius: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            background: "var(--ig-primary-soft)",
                            color: "var(--ig-primary)",
                            display: "grid",
                            placeItems: "center",
                            fontSize: 10,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {getInitial(r.user_name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "var(--ig-text)",
                            }}
                          >
                            @{r.user_name}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--ig-text)",
                              marginTop: 2,
                            }}
                          >
                            {r.text}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--ig-muted)",
                              marginTop: 2,
                            }}
                          >
                            {formatRelativeTime(r.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              ) : null}

              {/* 返信を作成 */}
              <SectionCard title="返信を作成">
                <textarea
                  className="ig-textarea"
                  placeholder="返信を入力..."
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  style={{ minHeight: 88, fontSize: 13 }}
                />
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--ig-muted)",
                    }}
                  >
                    {PENDING_FEATURE_HINT}
                  </span>
                  <button
                    type="button"
                    className="ig-button ig-button--primary"
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Send size={14} />
                    返信
                  </button>
                </div>
              </SectionCard>

              {/* アクション */}
              <SectionCard title="アクション">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 36, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <CheckCircle2 size={13} />
                    対応中に変更
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 36, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <CheckCircle2 size={13} color="var(--ig-success)" />
                    完了にする
                  </button>
                  <button
                    type="button"
                    className="ig-button ig-button--danger"
                    style={{ height: 36, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <AlertTriangle size={13} />
                    スパム報告
                  </button>
                  <button
                    type="button"
                    className="ig-button ig-button--danger"
                    style={{ height: 36, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Trash2 size={13} />
                    非表示にする
                  </button>
                </div>
              </SectionCard>

              {/* クイック返信 */}
              <SectionCard title="クイック返信">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 32, fontSize: 11 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Sparkles size={11} />
                    AI返信提案
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 32, fontSize: 11 }}
                    disabled
                  >
                    ありがとうございます!
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 32, fontSize: 11 }}
                    disabled
                  >
                    詳細は DM で送ります
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 32, fontSize: 11 }}
                    disabled
                  >
                    お問い合わせありがとうございます
                  </button>
                </div>
              </SectionCard>

              {/* 社内メモ */}
              <SectionCard title="社内メモ">
                <textarea
                  className="ig-textarea"
                  placeholder="社内向けのメモ(対応履歴には表示されません)"
                  defaultValue=""
                  disabled
                  title={PENDING_FEATURE_HINT}
                  style={{ minHeight: 60, fontSize: 12 }}
                />
              </SectionCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function CommentsHero({
  accountName,
  connected,
  onRefresh,
  loading,
}: {
  accountName: string | null;
  connected: boolean;
  onRefresh: () => void;
  loading?: boolean;
}) {
  return (
    <div className="ig-module-hero">
      <div className="ig-module-hero__row">
        <div>
          <div className="ig-module-hero__title">コメント管理</div>
          <div className="ig-module-hero__subtitle">
            投稿へのコメントを一括で対応
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AccountChip accountName={accountName} connected={connected} />
          <RefreshButton onClick={onRefresh} loading={loading} />
        </div>
      </div>
      <InstagramSubnav variant="hero" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// STATUS_LABEL 黙って使うと TS 警告出るので、空参照回避
// ---------------------------------------------------------------------------
void STATUS_LABEL;